import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { SesionCaducada, VisitaTerminada } from '../componentes/SesionCaducada'
import { ErrorDeLaApi, pedir, recuperarLaSesion, registrarRecuperadorDeSesion } from './nucleo'
import { crearRenovador, type ResultadoDeRenovar } from './renovacion'
import type { Usuario } from './tipos'
import { alPerderLaSesion, type EstadoDeVisita, msHastaElFin, type Visita, visitaDe } from './visita'

/**
 * La sesión es la cookie que ya emite el núcleo (`mc-token`, `HttpOnly`): el panel no
 * la lee, no la guarda y no la manda a mano — viaja sola porque el panel y la API
 * están en el mismo origen (docs/panel-propio.md §4). Nada en `localStorage`: un
 * token ahí lo lee cualquier guion que se cuele.
 *
 * Quién soy se pregunta una vez al arrancar y se cachea. Mientras el panel está en uso, la
 * sesión se renueva sola (`renovacion.ts`); y si aun así caduca, **no se sale de la
 * pantalla**: se abre «Tu sesión ha caducado» encima, se vuelve a entrar ahí mismo y lo que
 * había fallado se repite. Lo escrito en un formulario sigue donde estaba.
 *
 * Si la sesión era una **visita del equipo** a la cuenta de un cliente (CLAUDE.md §4), no se
 * pide la contraseña del cliente: se dice que la visita ha terminado —a su hora, aunque nadie
 * pulse nada— y se ofrece volver al panel del equipo (`visita.ts`).
 */
type Respuesta = { user: null | Usuario }

const Contexto = createContext<{ usuario: null | Usuario; cargando: boolean }>({ usuario: null, cargando: true })

/** Lo que caducó: quién estaba y, si era una visita del equipo a la cuenta de un cliente, cuál (`visita.ts`). */
type Caducada = { email: string; id: number; visita: null | Visita; resolver: (seguir: boolean) => void }

const MINUTO = 60_000
/** Un poco después de la hora de la visita: que el núcleo ya la dé por terminada cuando se pregunte. */
const MARGEN_AL_TERMINAR_MS = 2_000

export const SesionProvider = ({ children }: { children: ReactNode }) => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['sesion'],
    // `toleraAnonimo`: al arrancar, «no hay sesión» es una respuesta, no un fallo.
    queryFn: () => pedir<Respuesta>('/usuarios/me?depth=1', { toleraAnonimo: true }),
    retry: false,
    // Quién soy no se vuelve a preguntar solo: si la sesión caducara en segundo plano, un «nadie»
    // desmontaría la pantalla con lo escrito. Se actualiza al entrar, al salir y al volver a entrar.
    staleTime: Infinity,
    refetchOnReconnect: false,
  })
  const usuario = data?.user ?? null
  const dentro = usuario?.id ?? null
  // ¿Es una visita del equipo a la cuenta de un cliente? Se pregunta mientras la sesión vive: cuando
  // caduque ya no habrá a quién preguntárselo, y el diálogo tiene que saber qué ofrecer (`visita.ts`).
  const { data: estadoDeVisita } = useQuery({
    queryKey: ['visita', dentro],
    queryFn: () => pedir<EstadoDeVisita>('/suplantacion/estado', { toleraAnonimo: true }),
    enabled: dentro !== null,
    retry: false,
    staleTime: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })
  const visita = visitaDe(estadoDeVisita, usuario)
  const [caducada, setCaducada] = useState<Caducada | null>(null)

  const irAEntrar = useCallback(() => {
    clienteDeConsultas.clear()
    clienteDeConsultas.setQueryData(['sesion'], { user: null })
    navegar('/entrar', { replace: true, state: { volver: window.location.pathname + window.location.search } })
  }, [clienteDeConsultas, navegar])

  useEffect(() => {
    registrarRecuperadorDeSesion(async () => {
      const antes = clienteDeConsultas.getQueryData<Respuesta>(['sesion'])?.user
      if (!antes) {
        irAEntrar()
        return false
      }
      // ¿Se ha ido de verdad? Un 401 que no sea por la sesión no abre el diálogo.
      let ahora: Respuesta | null = null
      try {
        ahora = await pedir<Respuesta>('/usuarios/me?depth=1', { toleraAnonimo: true })
      } catch {
        return false
      }
      const visitaAntes = visitaDe(clienteDeConsultas.getQueryData<EstadoDeVisita>(['visita', antes.id]), antes)
      const decision = alPerderLaSesion({ antes, ahora: ahora.user, visita: visitaAntes })
      if (decision === 'seguir') {
        // La misma persona, quizá con otra visita que dura otra hora: se vuelve a preguntar.
        if (visitaAntes) void clienteDeConsultas.invalidateQueries({ queryKey: ['visita'] })
        return true
      }
      if (decision === 'otra-persona') {
        // En otra pestaña ha entrado otra persona: lo de esta pantalla era de la anterior.
        clienteDeConsultas.clear()
        clienteDeConsultas.setQueryData(['sesion'], ahora)
        navegar('/', { replace: true })
        return false
      }
      return new Promise<boolean>((resolver) => setCaducada({ email: antes.email, id: antes.id, visita: visitaAntes, resolver }))
    })
    return () => registrarRecuperadorDeSesion(null)
  }, [clienteDeConsultas, irAEntrar, navegar])

  // La renovación, mientras haya alguien dentro.
  useEffect(() => {
    if (dentro === null) return
    const renovar = async (): Promise<ResultadoDeRenovar> => {
      try {
        await pedir('/usuarios/refresh-token', { metodo: 'POST', toleraAnonimo: true })
        return 'ok'
      } catch (e) {
        if (!(e instanceof ErrorDeLaApi) || e.status >= 500) return 'fallo'
        try {
          return (await pedir<Respuesta>('/usuarios/me', { toleraAnonimo: true })).user ? 'no-renovable' : 'sin-sesion'
        } catch {
          return 'fallo'
        }
      }
    }
    const renovador = crearRenovador({
      renovar,
      alPerderla: () => void recuperarLaSesion(),
      visible: () => document.visibilityState === 'visible',
    })
    const reloj = window.setInterval(() => void renovador.alTic(), MINUTO)
    const alCambiar = () => {
      if (document.visibilityState === 'visible') void renovador.alVolver()
    }
    document.addEventListener('visibilitychange', alCambiar)
    return () => {
      window.clearInterval(reloj)
      document.removeEventListener('visibilitychange', alCambiar)
    }
  }, [dentro])

  // Una visita termina a su hora aunque nadie pulse nada: se dice en ese momento, no al primer
  // «Guardar» que falle. El núcleo no la renueva (la renovación lo intenta una vez y lo deja).
  const finDeLaVisita = visita?.expira ?? null
  useEffect(() => {
    if (finDeLaVisita === null) return
    const temporizador = window.setTimeout(() => void recuperarLaSesion(), msHastaElFin({ expira: finDeLaVisita }) + MARGEN_AL_TERMINAR_MS)
    return () => window.clearTimeout(temporizador)
  }, [finDeLaVisita])

  const alVolverAEntrar = (nuevo: Usuario) => {
    if (!caducada) return
    const { resolver, id } = caducada
    setCaducada(null)
    if (nuevo.id === id) {
      clienteDeConsultas.setQueryData(['sesion'], { user: nuevo })
      // Si era una visita y el equipo ha vuelto a entrar, esta dura otra hora: se vuelve a preguntar.
      void clienteDeConsultas.invalidateQueries({ queryKey: ['visita'] })
      resolver(true)
      return
    }
    // Ha entrado otra persona: lo que había en pantalla no es suyo.
    clienteDeConsultas.clear()
    clienteDeConsultas.setQueryData(['sesion'], { user: nuevo })
    navegar('/', { replace: true })
    resolver(false)
  }

  const alSalir = () => {
    if (!caducada) return
    const { resolver } = caducada
    setCaducada(null)
    irAEntrar()
    resolver(false)
  }

  return (
    <Contexto value={{ usuario, cargando: isPending }}>
      {children}
      {caducada &&
        (caducada.visita ? (
          // Una visita del equipo: sin el correo del cliente ni su contraseña, que el equipo no tiene.
          <VisitaTerminada alEntrar={alVolverAEntrar} alSalir={alSalir} nombre={caducada.visita.nombre} />
        ) : (
          <SesionCaducada alEntrar={alVolverAEntrar} alSalir={alSalir} email={caducada.email} />
        ))}
    </Contexto>
  )
}

export const useSesion = () => use(Contexto)

export const useEntrar = () => {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (datos: { email: string; password: string }) => pedir<{ user: Usuario }>('/usuarios/login', { metodo: 'POST', cuerpo: datos, toleraAnonimo: true }),
    onSuccess: (respuesta) => {
      clienteDeConsultas.setQueryData(['sesion'], { user: respuesta.user })
    },
  })
}

export const useSalir = () => {
  const clienteDeConsultas = useQueryClient()
  const navegar = useNavigate()
  return useMutation({
    mutationFn: () => pedir('/usuarios/logout', { metodo: 'POST', toleraAnonimo: true }),
    // Pase lo que pase con la petición, aquí dentro la sesión se acaba: si el servidor
    // no contesta, dejar al usuario «dentro» es peor que echarlo.
    onSettled: () => {
      clienteDeConsultas.clear()
      navegar('/entrar', { replace: true })
    },
  })
}

/** Los espacios del usuario, ya poblados por `?depth=1`. El superadmin no lleva lista: los ve todos. */
export const espaciosDe = (usuario: null | Usuario) =>
  (usuario?.clientes ?? [])
    .map((fila) => fila.cliente)
    .filter((cliente): cliente is Exclude<typeof cliente, number> => typeof cliente === 'object' && cliente !== null)
